import React, { useState, useEffect } from 'react';
import {
  Button,
  Flex,
  TextField,
  TextAreaField,
  Heading,
  View,
  Text,
  Badge,
  Card,
  Alert,
  SwitchField
} from '@aws-amplify/ui-react';

const ProjectForm = ({ project, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    numberRange: { min: 1, max: 100 },
    timeWindowEnabled: true, // NEW: Enable/disable time windows
    selectionTimeWindow: 24,
    status: 'Draft',
    allowCsvImport: false
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (project) {
      setFormData({
        ...project,
        // Convert minNumber/maxNumber to numberRange if needed
        numberRange: project.numberRange || { 
          min: project.minNumber || 1, 
          max: project.maxNumber || 100 
        },
        // Ensure timeWindowEnabled defaults to true for existing projects
        timeWindowEnabled: project.timeWindowEnabled !== undefined ? project.timeWindowEnabled : true,
        // Preserve the project ID for updates
        projectId: project.projectId || project.id
      });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle number fields specially to avoid NaN
    if (name === 'selectionTimeWindow') {
      const numValue = value === '' ? 0 : parseInt(value, 10);
      setFormData(prev => ({
        ...prev,
        [name]: isNaN(numValue) ? 0 : numValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleRangeChange = (e) => {
    const { name, value } = e.target;
    // Handle empty string case to avoid NaN
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setFormData(prev => ({
      ...prev,
      numberRange: {
        ...prev.numberRange,
        [name === 'minNumber' ? 'min' : 'max']: isNaN(numValue) ? 0 : numValue
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }
    
    if (formData.numberRange.min >= formData.numberRange.max) {
      setError('Maximum number must be greater than minimum number');
      return;
    }
    
    // NEW: Validate time window if enabled
    if (formData.timeWindowEnabled && (!formData.selectionTimeWindow || formData.selectionTimeWindow <= 0)) {
      setError('Selection time window must be greater than 0 hours when time limits are enabled');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <Card variation="elevated" padding="1.5rem">
      <Heading level={3}>{project ? 'Edit Project' : 'Create New Project'}</Heading>
      
      {error && (
        <Alert variation="error" isDismissible={true} onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="1rem" marginTop="1rem">
          <TextField
            label="Project Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          
          <TextAreaField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
          />
          
          <Flex gap="1rem">
            <TextField
              label="Minimum Number"
              name="minNumber"
              type="number"
              value={formData.numberRange.min}
              onChange={handleRangeChange}
              required
            />
            
            <TextField
              label="Maximum Number"
              name="maxNumber"
              type="number"
              value={formData.numberRange.max}
              onChange={handleRangeChange}
              required
            />
          </Flex>
          
          {/* NEW: Time Window Toggle */}
          <Card variation="outlined" padding="1rem" backgroundColor="var(--amplify-colors-background-secondary)">
            <Heading level={5} marginBottom="0.5rem">Selection Time Limits</Heading>
            
            <SwitchField
              label="Enable Selection Time Limits"
              name="timeWindowEnabled"
              isChecked={formData.timeWindowEnabled}
              onChange={(e) => setFormData(prev => ({ ...prev, timeWindowEnabled: e.target.checked }))}
            />
            <Text fontSize="0.9rem" color="gray" marginTop="-0.5rem" marginBottom="1rem">
              When enabled, participants must select their number within the specified time window or they will be automatically skipped.
            </Text>
            
            {formData.timeWindowEnabled && (
              <TextField
                label="Selection Time Window (hours)"
                name="selectionTimeWindow"
                type="number"
                value={formData.selectionTimeWindow}
                onChange={handleChange}
                required
                min="1"
                max="168" // 1 week max
              />
            )}
            
            {!formData.timeWindowEnabled && (
              <Alert variation="info" marginTop="0.5rem">
                <Text fontSize="0.9rem">
                  <strong>No Time Limits:</strong> Participants can take as long as they need to make their selection. 
                  They will still receive turn notifications, but won't be automatically skipped.
                </Text>
              </Alert>
            )}
          </Card>
          
          <SwitchField
            label="Allow CSV Import"
            name="allowCsvImport"
            isChecked={formData.allowCsvImport}
            onChange={(e) => setFormData(prev => ({ ...prev, allowCsvImport: e.target.checked }))}
          />
          <Text fontSize="0.9rem" color="gray" marginTop="-0.5rem">
            When enabled, administrators can import participants from CSV files even after participants have been added manually.
          </Text>
          
          <Flex justifyContent="space-between" marginTop="1rem">
            <Button type="button" onClick={onCancel}>
              Cancel
            </Button>
            
            <Button type="submit" variation="primary">
              {project ? 'Update Project' : 'Create Project'}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Card>
  );
};

export default ProjectForm;
